// ./src/components/features/Submissions/SubmissionDetailsDrawer/SubmissionMetadata.tsx
import React, { useMemo, useState } from "react";
import {
  Stack,
  Text,
  Badge,
  Group,
  Tooltip,
  Collapse,
  UnstyledButton,
  Box,
  CopyButton,
  ActionIcon,
} from "@mantine/core";
import { Submission } from "@/utils/api";
import { formatDateTime, parseUserAgent } from "@/utils";
import { SubmissionStatusBadge } from "../SubmissionStatusBadge";
import { SubmissionStatusChanger } from "../SubmissionStatusChanger";
import { AppIcon } from "@/components/generic";

export interface SubmissionMetadataProps {
  /** Submission to display */
  submission: Submission | null;

  /** Allow status changes */
  allowStatusChange?: boolean;

  /** Callback when status is changed */
  onStatusChanged?: (newStatus: number) => void;
}

/**
 * Labels for spam reason codes
 */
const SPAM_REASON_LABELS: Record<string, { label: string; color: string }> = {
  gibberish: { label: "Gibberish", color: "orange" },
  external_api: { label: "External API", color: "red" },
  local_model: { label: "Local Model", color: "red" },
  honeypot: { label: "Honeypot", color: "grape" },
  ip_filter: { label: "IP Blocked", color: "red" },
  ai_spam: { label: "AI Spam", color: "red" }, // <-- ADD THIS
};

interface ParsedSpamReason {
  type: string;
  label: string;
  color: string;
  tooltip?: string;
  score?: number;
}

const getScoreColor = (score: number): string => {
  if (score <= 30) return "green";
  if (score <= 50) return "yellow";
  if (score <= 80) return "orange";
  return "red";
};

const parseSpamReason = (reason: string): ParsedSpamReason => {
  // Format: gibberish:fieldLabel:level:source:score
  if (reason.startsWith("gibberish:")) {
    const parts = reason.split(":");
    if (parts.length >= 5) {
      const [, fieldLabel, level, source, scoreStr] = parts;
      const score = parseInt(scoreStr, 10);
      return {
        type: "gibberish",
        label: `Gibberish: ${fieldLabel}`,
        color: getScoreColor(isNaN(score) ? 100 : score),
        tooltip: `Level: ${level} (${source})`,
        score: isNaN(score) ? undefined : score,
      };
    }
  }

  // Format: ai_spam:score:explanation
  if (reason.startsWith("ai_spam:")) {
    const parts = reason.split(":");
    if (parts.length >= 3) {
      const [, scoreStr, ...explanationParts] = parts;
      const score = parseInt(scoreStr, 10);
      const explanation = explanationParts.join(":"); // Rejoin in case explanation has colons
      return {
        type: "ai_spam",
        label: "AI Spam",
        color: getScoreColor(isNaN(score) ? 100 : score),
        tooltip: explanation || "Flagged by AI analysis",
        score: isNaN(score) ? undefined : score,
      };
    }
  }

  // Format: ip_filter:mode:clientIp
  if (reason.startsWith("ip_filter:")) {
    const parts = reason.split(":");
    if (parts.length >= 3) {
      const [, mode, clientIp] = parts;
      return {
        type: "ip_filter",
        label: mode === "allow" ? "IP Not Allowed" : "IP Denied",
        color: "red",
        tooltip: `IP: ${clientIp}`,
      };
    }
  }

  const reasonInfo = SPAM_REASON_LABELS[reason] || {
    label: reason,
    color: "gray",
  };

  return {
    type: reason,
    label: reasonInfo.label,
    color: reasonInfo.color,
  };
};

/** Row with label and value */
const MetaRow: React.FC<{
  label: string;
  value: React.ReactNode;
  copyable?: string;
}> = ({ label, value, copyable }) => (
  <Group gap="sm" wrap="nowrap" align="flex-start">
    <Text size="md" c="dimmed" style={{ minWidth: 90 }}>
      {label}
    </Text>
    <Group gap={4} wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
      <Box style={{ flex: 1, minWidth: 0 }}>
        {typeof value === "string" ? (
          <Text size="md" style={{ wordBreak: "break-word" }}>
            {value}
          </Text>
        ) : (
          value
        )}
      </Box>
      {copyable && (
        <CopyButton value={copyable}>
          {({ copied, copy }) => (
            <Tooltip label={copied ? "Copied" : "Copy"} withArrow>
              <ActionIcon
                size="xs"
                variant="subtle"
                color={copied ? "teal" : "gray"}
                onClick={copy}
              >
                <AppIcon iconName={copied ? "Check" : "Copy"} size={12} />
              </ActionIcon>
            </Tooltip>
          )}
        </CopyButton>
      )}
    </Group>
  </Group>
);

export const SubmissionMetadata: React.FC<SubmissionMetadataProps> = ({
  submission,
  allowStatusChange = false,
  onStatusChanged,
}) => {
  const [opened, setOpened] = useState(true);

  // Parse user agent details
  const userAgentDetails = useMemo(() => {
    return parseUserAgent(submission?.userAgent);
  }, [submission?.userAgent]);

  // Format device info
  const deviceInfo = useMemo(() => {
    const parts: string[] = [];
    if (userAgentDetails.browser !== "Unknown")
      parts.push(userAgentDetails.browser);
    if (userAgentDetails.os !== "Unknown") parts.push(userAgentDetails.os);
    if (
      userAgentDetails.device !== "Unknown" &&
      userAgentDetails.device !== "Desktop"
    )
      parts.push(userAgentDetails.device);
    return parts.length > 0 ? parts.join(" · ") : null;
  }, [userAgentDetails]);

  // Check if there's any spam protection data
  const hasSpamData =
    submission?.spamReason ||
    (submission?.gibberishScore !== undefined &&
      submission.gibberishScore > 0) ||
    (submission?.spamScore !== undefined && submission.spamScore > 0);

  // Parse spam reasons
  const parsedSpamReasons = useMemo(() => {
    if (!submission?.spamReason) return [];
    return submission.spamReason
      .split(",")
      .map((r) => r.trim())
      .filter((r) => r.length > 0)
      .map(parseSpamReason);
  }, [submission?.spamReason]);

  return (
    <Stack gap="xs">
      {/* Header with toggle */}
      <UnstyledButton onClick={() => setOpened((o) => !o)} w="100%">
        <Group justify="space-between" align="center">
          <Text size="sm" fw={600} tt="uppercase" c="dimmed">
            Submission Details
          </Text>
          <AppIcon
            iconName={opened ? "ChevronUp" : "ChevronDown"}
            size={16}
            color="var(--mantine-color-gray-4)"
          />
        </Group>
      </UnstyledButton>

      <Collapse in={opened}>
        <Stack gap="sm" pt="xs">
          {/* Status - own line */}
          <Group gap="sm" wrap="nowrap" align="center">
            <Text size="md" c="dimmed" style={{ minWidth: 90 }}>
              Status
            </Text>
            {allowStatusChange && submission?.id ? (
              <SubmissionStatusChanger
                submissionId={submission.id}
                currentStatus={submission.status}
                onStatusChanged={onStatusChanged}
                size="sm"
              />
            ) : (
              <SubmissionStatusBadge status={submission?.status} size="sm" />
            )}
          </Group>

          {/* Request ID - prominent */}
          {submission?.requestId && (
            <MetaRow
              label="Request ID"
              value={submission.requestId}
              copyable={submission.requestId}
            />
          )}

          {/* Date/Time */}
          <MetaRow
            label="Submitted"
            value={
              submission?.submissionDttm
                ? formatDateTime(submission.submissionDttm, "datetime")
                : "—"
            }
          />

          {/* IP Address */}
          {submission?.remoteIp && (
            <MetaRow
              label="IP Address"
              value={submission.remoteIp}
              copyable={submission.remoteIp}
            />
          )}

          {/* Referrer */}
          {submission?.referrer && (
            <MetaRow label="Referrer" value={submission.referrer} />
          )}

          {/* Device */}
          {deviceInfo && <MetaRow label="Device" value={deviceInfo} />}

          {/* Spam Protection - one badge per reason with score included */}
          {hasSpamData && (
            <Group gap="sm" wrap="nowrap" align="flex-start">
              <Text size="md" c="dimmed" style={{ minWidth: 90 }}>
                Spam
              </Text>
              <Group gap="xs" wrap="wrap" align="center">
                {/* Reason badges with scores */}
                {parsedSpamReasons.map((reason, index) => (
                  <Tooltip
                    key={index}
                    label={reason.tooltip}
                    disabled={!reason.tooltip}
                  >
                    <Badge variant="light" color={reason.color} size="md">
                      {reason.label}
                      {reason.score !== undefined ? ` ${reason.score}%` : ""}
                    </Badge>
                  </Tooltip>
                ))}

                {/* Engine badge - show the actual engine identifier */}
                {submission?.spamEngine && (
                  <Tooltip label={`Analyzed by ${submission.spamEngine}`}>
                    <Badge variant="outline" color="blue" size="md">
                      {submission.spamEngine.startsWith("openai-")
                        ? `OpenAI ${submission.spamEngine.replace(
                            "openai-",
                            ""
                          )}`
                        : submission.spamEngine.startsWith("claude-")
                        ? `Claude ${submission.spamEngine.replace(
                            "claude-",
                            ""
                          )}`
                        : submission.spamEngine === "external"
                        ? "External API"
                        : submission.spamEngine === "local"
                        ? "Local Model"
                        : submission.spamEngine === "both"
                        ? "External + Local"
                        : submission.spamEngine}
                    </Badge>
                  </Tooltip>
                )}
              </Group>
            </Group>
          )}
        </Stack>
      </Collapse>
    </Stack>
  );
};
